import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { addProfile, CamelCaseNamingConvention, createMap, forMember, ignore, mapFrom, MappingProfile, namingConventions, typeConverter } from '@automapper/core';
import { Temporal } from '@js-temporal/polyfill';
import { AppModule } from './app/app.module';
import { AreaMap } from './app/domain/area-map';
import { MaterielEau } from './app/domain/EAU/materiel-eau';
import { MeasureEau } from './app/domain/EAU/measure-eau';
import { Intervention } from './app/domain/intervention';
import { MeasureLingette } from './app/domain/Lingette/measure-lingette';
import { MeasureMassif } from './app/domain/Massif/measure-massif';
import { Process } from './app/domain/process';
import { ProcessMeasure } from './app/domain/process-mesure';
import { SamplingPoint } from './app/domain/sampling-point';
import { User } from './app/domain/user';
import { Zone } from './app/domain/zone';
import { ZoneMeasure } from './app/domain/zone-measure';
import { AreaMapDTO } from './app/services/dto/area-map.dto';
import { InterventionDTO } from './app/services/dto/intervention.dto';
import { MaterielEauDTO } from './app/services/dto/materiel-eau.dto';
import { MeasureEauDTO } from './app/services/dto/measure-eau.dto';
import { MeasureLingetteDTO } from './app/services/dto/measure-lingette.dto';
import { MeasureMassifDTO } from './app/services/dto/measure-massif.dto';
import { ProcessMeasureDTO } from './app/services/dto/process-measure.dto';
import { ProcessDTO } from './app/services/dto/process.dto';
import { SamplingPointDTO } from './app/services/dto/sampling-point.dto';
import { UserDTO } from './app/services/dto/user.dto';
import { ZoneMeasureDTO } from './app/services/dto/zone-measure.dto';
import { ZoneDTO } from './app/services/dto/zone.dto';
import { mapper } from './app/tools/mapping/mapper';
import { environment } from './environments/environment';
import { AgencyDTO } from './app/services/dto/agency.dto';
import { Agency } from './app/domain/agency';
import { EventDTO } from './app/services/dto/event.dto';
import { Event } from './app/domain/event';
import { StrategyDTO } from './app/services/dto/strategy.dto';
import { DisponibiliteClientDTO } from './app/services/dto/disponibilite.dto';
import { DisponibiliteClient } from './app/domain/disponibilite-client';
import { PlanningConfiguration } from './app/domain/planning-configuration';
import { PlanInterventionDTO } from './app/services/plan-intervention.dto';
import { PlanningConfigurationDTO } from './app/services/dto/planning-configuration.dto';
import { MesureGenericDTO } from './app/services/dto/mesure-generic.dto';
import { MesureGeneric } from './app/domain/mesure-generique';
import { CompetenceDTO } from './app/services/dto/competence.dto';
import { Competence } from './app/domain/competence';
import { WorkDTO } from './app/services/dto/work.dto';
import { Work } from './app/domain/GS/work';
import { Portfolio } from './app/domain/GS/portfolio';
import { PortfolioDTO } from './app/services/dto/portfolio.dto';
import { Strategy } from './app/domain/GS/strategy';
import { SamplerAddressDTO } from './app/services/dto/sampler-address.dto';
import { SamplerAddress } from './app/model/sampler-address';
import { MesureAdductionDTO } from './app/services/dto/mesure-adduction.dto';
import { MesureAdduction } from './app/domain/Adduction-air/mesure-adduction';
import { ELIMSJobInfoDTO } from './app/services/dto/elims-job-info.dto';
import { ELIMSJobInfo } from './app/domain/elims-job-info';
import { Role } from './app/domain/user-profile/role';
import { RoleDTO } from './app/services/dto/role.dto';
import { PermissionsDTO } from './app/services/dto/permissions.dto';
import { Permissions } from './app/domain/user-profile/permissions';

if (environment.production) {
    enableProdMode();
}

platformBrowserDynamic().bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()], })
    .catch(err => console.error(err));

// DTO -> DOM
const dtoProfile: MappingProfile = (mapper) => {
    createMap(mapper, InterventionDTO, Intervention,
        typeConverter(String, Temporal.PlainTime, (date) => date ? Temporal.PlainTime.from(date) : undefined),
        typeConverter(String, Temporal.PlainDateTime, (date) => date ? Temporal.PlainDateTime.from(date) : undefined));

    createMap(mapper, UserDTO, User)
    createMap(mapper, PlanningConfigurationDTO, PlanningConfiguration)
    createMap(mapper, ELIMSJobInfoDTO, ELIMSJobInfo)
    createMap(mapper, SamplingPointDTO, SamplingPoint)

    createMap(mapper, MeasureEauDTO, MeasureEau,
        typeConverter(String, Temporal.PlainTime, (date) => date ? Temporal.PlainTime.from(date) : undefined));
    createMap(mapper, MaterielEauDTO, MaterielEau,
        typeConverter(String, Temporal.PlainDate, (date) => date ? Temporal.PlainDate.from(date) : undefined));
    createMap(mapper, MeasureMassifDTO, MeasureMassif,
        typeConverter(String, Temporal.PlainTime, (date) => date ? Temporal.PlainTime.from(date) : undefined));
    createMap(mapper, MeasureLingetteDTO, MeasureLingette,
        typeConverter(String, Temporal.PlainTime, (date) => date ? Temporal.PlainTime.from(date) : undefined));
    createMap(mapper, MesureAdductionDTO, MesureAdduction,
        typeConverter(String, Temporal.PlainTime, (date) => date ? Temporal.PlainTime.from(date) : undefined));
    createMap(mapper, MesureGenericDTO, MesureGeneric,
        typeConverter(String, Temporal.PlainTime, (date) => date ? Temporal.PlainTime.from(date) : undefined));

    createMap(mapper, ZoneDTO, Zone,
        forMember((dest) => dest.CustomerReference, mapFrom(source => source.customerRef)),
        forMember((dest) => dest.NonStandardBuildingType, mapFrom(source => source.buildingTypeNonStandard)),
        forMember((dest) => dest.NonStandardRoomUsage, mapFrom(source => source.roomUsageNonStandard)))

    createMap(mapper, ZoneMeasureDTO, ZoneMeasure,
        forMember((dest) => dest.IsCancelled, ignore()),
        forMember((dest) => dest.Color, mapFrom(source => '#' + source.color)),
        forMember((dest) => dest.TextColor, mapFrom(source => '#' + source.textColor)));

    createMap(mapper, ProcessDTO, Process)
    createMap(mapper, ProcessMeasureDTO, ProcessMeasure,
        forMember((dest) => dest.IsCancelled, ignore()),
        forMember((dest) => dest.Color, mapFrom(source => '#' + source.color)),
        forMember((dest) => dest.TextColor, mapFrom(source => '#' + source.textColor)));

    createMap(mapper, StrategyDTO, Strategy)
    createMap(mapper, WorkDTO, Work)
    createMap(mapper, PortfolioDTO, Portfolio)
    // forMember((dest) => dest.Code, mapFrom(source => source.id)))

    createMap(mapper, AreaMapDTO, AreaMap)
    createMap(mapper, AgencyDTO, Agency)
    createMap(mapper, EventDTO, Event,
        typeConverter(String, Temporal.PlainDateTime, (date) => date ? Temporal.PlainDateTime.from(date) : undefined))

    createMap(mapper, DisponibiliteClientDTO, DisponibiliteClient)

    createMap(mapper, CompetenceDTO, Competence)
    createMap(mapper, SamplerAddressDTO, SamplerAddress)

    createMap(mapper, AreaMap, AreaMap)

    createMap(mapper, RoleDTO, Role)
    createMap(mapper, PermissionsDTO, Permissions)
};
addProfile(
    mapper,
    dtoProfile,
    namingConventions(new CamelCaseNamingConvention())
);